/**
 * House Loan Calculator Page
 * Standalone page for house loan calculations
 */
import { useState, useEffect } from 'react';
import { useCustomTranslation } from '../../hooks';
import {
  HouseLoanCalculatorForm,
  HouseLoanResults,
  type HouseLoanData,
  type HouseLoanResultsData
} from '../../components/features/calculator';
import { trackPageView, trackError, trackHouseLoanCalculation, trackConversion } from '../../utils/analytics';
import AmazonRecommendation from '../../components/common/AmazonRecommendation';
import SEO from '../../components/common/SEO';

export default function HouseLoanPage() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';
  const [results, setResults] = useState<HouseLoanResultsData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    trackPageView('/calculators/house-loan', 'House Loan Calculator');
    document.title = `${t('calculators.houseLoan.title')} | PayWise365`;
  }, [t]);

  const seoTitle = isEnglish
    ? 'Free House Loan Calculator Hong Kong 2026 - Calculate Mortgage Payments | PayWise365'
    : '免費樓宇貸款計算機香港 2026 - 計算按揭供款 | PayWise365';

  const seoDescription = isEnglish
    ? 'Calculate your Hong Kong house loan monthly payments instantly. Free mortgage calculator with detailed amortization schedule, total interest, and payment breakdowns. Supports different payment frequencies and loan terms.'
    : '立即計算您的香港樓宇貸款每月供款。免費按揭計算機，提供詳細還款時間表、總利息和供款明細。支援不同供款頻率和貸款期限。';

  const seoKeywords = isEnglish
    ? 'house loan calculator Hong Kong, mortgage calculator HK, 按揭計算機, monthly payment calculator, loan amortization, mortgage payment HK, free calculator 2026'
    : '樓宇貸款計算機, 香港按揭計算機, 每月供款計算, 還款時間表, 按揭供款, 免費計算器 2026';

  const handleCalculate = (data: HouseLoanData) => {
    setLoading(true);

    try {
      const monthlyRate = data.annual_interest_rate / 100 / 12;
      const totalMonths = data.loan_term_years * 12;

      const monthlyPayment = data.loan_amount *
        (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
        (Math.pow(1 + monthlyRate, totalMonths) - 1);

      const totalPayment = monthlyPayment * totalMonths;
      const totalInterest = totalPayment - data.loan_amount;

      const calculatedResults: HouseLoanResultsData = {
        monthly_payment: monthlyPayment,
        total_payment: totalPayment,
        total_interest: totalInterest,
        loan_amount: data.loan_amount,
        annual_interest_rate: data.annual_interest_rate,
        loan_term_years: data.loan_term_years,
        loan_term_months: totalMonths
      };

      setResults(calculatedResults);

      // Track successful calculation
      trackHouseLoanCalculation({
        loan_amount: data.loan_amount,
        interest_rate: data.annual_interest_rate,
        loan_term_years: data.loan_term_years,
        monthly_payment: monthlyPayment,
        total_interest: totalInterest,
      });

      // Track conversion
      trackConversion('house_loan_calculation_completed');
    } catch (err: any) {
      trackError('house_loan_calculation_error', err.message, 'calculation_logic');
      console.error('House loan calculation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="calculator-page">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://www.paywise365.com/calculators/house-loan"
        ogType="website"
      />
      <div className="page-header">
        <div className="page-header-content">
          <h1>{t('calculators.houseLoan.title')}</h1>
          <p>{t('calculators.houseLoan.pageSubtitle')}</p>
        </div>
      </div>

      <main className="calculator-content">
        <div className="calculator-container">
          <HouseLoanCalculatorForm
            onCalculate={handleCalculate}
            isLoading={loading}
          />
          {results && <HouseLoanResults results={results} />}
        </div>
      </main>
      <div style={{ width: '100%', padding: '30px 20px' }}>
        <AmazonRecommendation isEnglish={isEnglish} />
      </div>
    </div>
  );
}
